import { FC, memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { resources } from 'src/config/resources';
import { ROUTE_MY_ACCOUNT_PAGE } from 'src/routes';
import { commonSelector } from 'src/store/commonPart/selector';
import { getDOMElement } from 'src/utils/DOM';
import logo from 'src/assets/images/footer-logo.png';
import { stringArrayStartsWith } from 'src/utils/stringArrayStartsWith';
import { UiOuterLink } from 'src/components/UI/OuterLink';
import { Enums } from 'src/models/enums';

declare global {
  interface Window {
    isDonorFacingPage: boolean;
  }
}

export const Footer: FC = memo(
  () => {
    const {
      currentPartner: { response },
      sidebarOpen,
      brandingModel,
      organizationSubscriptionType,
      isErrorPageShown,
    } = useSelector(commonSelector);

    const { t } = useTranslation();
    const { pathname } = useLocation();

    const plusSubscriptions: number[] = useMemo(
      () => [
        Enums.organizations.OrganizationSubscriptionTypeCode.Plus,
        Enums.organizations.OrganizationSubscriptionTypeCode.Trial,
      ],
      []
    );

    const showFooter = useMemo(() => {
      if (isErrorPageShown) {
        return true;
      }

      // eslint-disable-next-line camelcase
      const PlUS_SUBSCRIPTION_FOOTER_HIDDEN_PATHS = [ROUTE_MY_ACCOUNT_PAGE];

      if (
        plusSubscriptions.includes(organizationSubscriptionType) &&
        (window.isDonorFacingPage || stringArrayStartsWith(pathname, PlUS_SUBSCRIPTION_FOOTER_HIDDEN_PATHS))
      ) {
        return false;
      }

      const FOOTER_HIDDEN_PATHS = ['/campaigns/donationpagesetup/', '/organization/setupbranding'];

      /** blacklist of pages on which footer mustn't be shown */
      if (pathname === '/organization/verify' || stringArrayStartsWith(pathname, FOOTER_HIDDEN_PATHS)) {
        return false;
      }
      return getDOMElement('.setup-nav-gray-ribbon') === null;
    }, [plusSubscriptions, isErrorPageShown, organizationSubscriptionType, pathname]);

    const poweredByShown = useMemo(() => {
      if (isErrorPageShown) {
        return true;
      }

      const calc = () => {
        const lowerPathname = pathname.toLowerCase();

        const fitsKnownOneStringUrls = !![
          '/contributions',
          '/checkout',
          '/teammemberfullpage',
          '/me',
          '/teamfullpage',
          '/team',
          '/membercenter',
          '/home',
          '/organizationhomepage',
          '/startpage',
          '/registerorganizationpage',
          '/campaignpage',
          '/teammemberpage',
          '/teampage',
          '/administration',
          '/partner',
          '/paymentrecoverypage',
          '/donationpage',
          '/campaigns',
          '/register',
        ].find((url) => lowerPathname.startsWith(url));

        return !(fitsKnownOneStringUrls && !lowerPathname.startsWith('/checkout')
          ? false
          : matchPath(pathname, { path: '/:id', exact: true }) ||
            matchPath(pathname, '/:id/campaign') ||
            matchPath(pathname, '/:id/m/:esheid/') ||
            matchPath(pathname, '/:id/t/:esheid/') ||
            matchPath(pathname, '/c/:id') ||
            matchPath(pathname, '/checkout/contributionconfirmation/:id') ||
            matchPath(pathname, '/checkout/confirmation/:id') ||
            matchPath(pathname, ROUTE_MY_ACCOUNT_PAGE) ||
            matchPath(pathname, '/account/organizer/sweepstakeofflineentries/:id'));
      };

      return brandingModel.IsPlusSubscriptionEnabled && plusSubscriptions.includes(organizationSubscriptionType)
        ? calc()
        : true;
    }, [
      brandingModel.IsPlusSubscriptionEnabled,
      plusSubscriptions,
      isErrorPageShown,
      organizationSubscriptionType,
      pathname,
    ]);

    return showFooter ? (
      <footer>
        <div className={classNames('container', { shift: sidebarOpen })}>
          {response?.EnablePoweredByLabel && brandingModel.FooterVisible && poweredByShown && (
            <div className="powered-by-label" data-testid="powered-by-label">
              <span>{t('footer.poweredBy')}</span>
              <UiOuterLink href={resources.mainIndexPath}>
                <img src={logo} alt={resources.mainIndexPath} />
              </UiOuterLink>
            </div>
          )}
        </div>
      </footer>
    ) : null;
  },
  () => true
);
